/**
 * Formatting + date helpers for the Event Reporting pages.
 *
 * All calendar logic is in ASHI's time zone (America/Chicago): LP records
 * appointments in local time, and "today" for the default filter window is
 * ASHI's today, not the viewer's.
 */
import type { EventAppointment } from "../lib/eventReporting";

export const ASHI_TZ = "America/Chicago";

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usdCents = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const ymd = new Intl.DateTimeFormat("en-CA", { timeZone: ASHI_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const apptFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: ASHI_TZ, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
});
const stampFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: ASHI_TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
});

export function money(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : usd.format(v);
}

export function moneyCents(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : usdCents.format(v);
}

export function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? "Not set" : `${(v * 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
}

/** YYYY-MM-DD for "now" in ASHI's time zone. */
export function todayYmd(): string {
  return ymd.format(new Date());
}

export function addDays(ymdStr: string, days: number): string {
  const d = new Date(`${ymdStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The report date the filters use: appointment day in ASHI time, else entry date. Mirrors the API. */
export function reportDate(r: EventAppointment): string | null {
  return r.apptdate ? ymd.format(new Date(r.apptdate)) : r.entrydate;
}

export function apptLabel(iso: string | null): string {
  return iso ? apptFmt.format(new Date(iso)) : "";
}

export function stampLabel(iso: string | null): string {
  return iso ? stampFmt.format(new Date(iso)) : "never";
}
